/*
 * Reconciles the cart to the free-book plan rendered by
 * snippets/cart-gift-books.liquid.
 *
 * The rule itself is not here. Liquid decides, on the server, which books a
 * cart has earned and what is wrong with the ones already in it; this script
 * only carries out the operations it is handed. That is the same split the
 * Conversation Lab gate uses in main-cart-footer.liquid, and it is what keeps
 * the two from ever disagreeing.
 *
 * Runs only on the cart page — the plan element exists nowhere else.
 */
(function () {
  var PLAN_SELECTOR = '[data-cart-gift-plan]';
  var ROW_SELECTOR = '.cart-cards__row';
  var ROW_BUSY_CLASS = 'cart-cards__row--busy';
  var ITEMS_ID = 'main-cart-items';
  var FOOTER_ID = 'main-cart-footer';

  // A pass that changes the cart produces a fresh plan, which is re-read and
  // applied again. A correct plan converges on the second read; more than this
  // means something is not settling (a variant that refuses to add, a Cart
  // Transform disagreeing), and looping forever would be worse than stopping.
  var MAX_PASSES = 4;
  var passes = 0;
  var running = false;

  // Which rows the pass in flight is about to change, so the spinner can be put
  // back after the section re-render wipes the DOM mid-pass.
  var busyTargets = { keys: [], variants: [] };

  function readPlan(root) {
    var el = (root || document).querySelector(PLAN_SELECTOR);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (error) {
      return null;
    }
  }

  function planSize(plan) {
    if (!plan) return 0;
    return ['add', 'decrement', 'remove', 'revert', 'repoint', 'clamp'].reduce(function (total, key) {
      return total + ((plan[key] && plan[key].length) || 0);
    }, 0);
  }

  function fetchConfigJSON() {
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
    };
  }

  // /cart/change.js addresses a line by its key rather than its index: an index
  // shifts the moment a decrement empties a line, and this plan can empty one.
  function changeLine(op, properties) {
    var body = { id: op.key, quantity: op.quantity };
    // Omitting `properties` leaves them untouched; an empty object clears them,
    // which is exactly how a gift line reverts to an ordinary paid one.
    if (properties !== undefined) body.properties = properties;
    return fetch(window.Shopify.routes.root + 'cart/change.js', Object.assign(fetchConfigJSON(), { body: JSON.stringify(body) }));
  }

  function applyPlan(plan) {
    // Sequential on purpose. The Cart AJAX API serialises writes per session,
    // and overlapping calls read each other's half-applied state.
    var chain = Promise.resolve();

    // Gifts whose class has gone. Quantity 0 is how the Cart AJAX API deletes a
    // line, so this is the same call as every other op, not a special route.
    (plan.remove || []).forEach(function (op) {
      chain = chain.then(function () { return changeLine(op); });
    });

    (plan.decrement || []).forEach(function (op) {
      chain = chain.then(function () { return changeLine(op); });
    });
    // Not cleared to {}: the line keeps one marker so the card can say why a
    // book that was free a moment ago is being charged for. The Cart Transform
    // matches on _free_with_class, which is gone, so it stops zeroing the line.
    (plan.revert || []).forEach(function (op) {
      chain = chain.then(function () { return changeLine(op, { _was_free: '1' }); });
    });
    (plan.repoint || []).forEach(function (op) {
      chain = chain.then(function () { return changeLine(op, op.properties); });
    });
    (plan.clamp || []).forEach(function (op) {
      chain = chain.then(function () { return changeLine(op); });
    });

    if (plan.add && plan.add.length) {
      chain = chain.then(function () {
        return fetch(
          window.Shopify.routes.root + 'cart/add.js',
          Object.assign(fetchConfigJSON(), { body: JSON.stringify({ items: plan.add }) })
        );
      });
    }

    return chain;
  }

  /*
   * Re-renders the cart from the server rather than patching the DOM by hand.
   * The gift rows, the subtotal, the Lab gate and the line count are all
   * server-rendered, and a book that just changed price has to come back
   * through Liquid to show it.
   */
  function rerender() {
    var items = document.getElementById(ITEMS_ID);
    var sectionId = items ? items.dataset.id : null;
    if (!sectionId) return Promise.resolve(null);

    return fetch(window.location.pathname + '?section_id=' + encodeURIComponent(sectionId))
      .then(function (response) { return response.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var source = doc.querySelector('cart-items');
        var target = document.querySelector('cart-items');
        if (source && target) target.innerHTML = source.innerHTML;
        return doc;
      });
  }

  // cart.js only refreshes #cart-icon-bubble, which belongs to the retired
  // header. header-v2 keeps its count in its own element, so without this the
  // badge would still show the pre-gift number until the next full page load.
  function syncCartCount(count) {
    document.querySelectorAll('.header-v2__cart-link').forEach(function (link) {
      var badge = link.querySelector('.header-v2__cart-count');
      if (count > 0) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'header-v2__cart-count';
          badge.setAttribute('aria-hidden', 'true');
          (link.querySelector('.header-v2__cart-icon-wrap') || link).appendChild(badge);
        }
        badge.textContent = count;
      } else if (badge) {
        badge.remove();
      }
      link.setAttribute('aria-label', 'Cart (' + count + ' items)');
    });

    document
      .querySelectorAll('#cart-icon-bubble .cart-count-bubble span[aria-hidden="true"]')
      .forEach(function (el) { el.textContent = count < 100 ? count : ''; });

    var title = document.querySelector('.cart-cards__title');
    if (title) title.textContent = title.textContent.replace(/\(\d+\)/, '(' + count + ')');
  }

  /*
   * The rows a plan is about to touch.
   *
   * An operation on a line that already exists names it by `key`; an `add` has
   * no line yet, so it can only name the variant it is about to create. Both
   * are needed, and for the common case — a book already in the cart being
   * turned free — they resolve to the same row twice, once as the paid line
   * giving up a unit and once as the gift about to replace it.
   */
  function collectBusyTargets(plan) {
    var keys = [];
    var variants = [];

    ['decrement', 'remove', 'revert', 'repoint', 'clamp'].forEach(function (name) {
      (plan[name] || []).forEach(function (op) {
        if (op && op.key != null) keys.push(String(op.key));
      });
    });

    (plan.add || []).forEach(function (op) {
      if (op && op.id != null) variants.push(String(op.id));
    });

    return { keys: keys, variants: variants };
  }

  // Idempotent, because it runs again after every re-render: the fresh rows
  // carry the same keys, so the same lines light up without anything to undo.
  function markBusyRows() {
    document.querySelectorAll(ROW_SELECTOR).forEach(function (row) {
      var busy =
        busyTargets.keys.indexOf(row.dataset.cartLineKey) !== -1 ||
        busyTargets.variants.indexOf(row.dataset.cartVariantId) !== -1;
      row.classList.toggle(ROW_BUSY_CLASS, busy);
    });
  }

  function clearBusyRows() {
    document.querySelectorAll(ROW_SELECTOR + '.' + ROW_BUSY_CLASS).forEach(function (row) {
      row.classList.remove(ROW_BUSY_CLASS);
    });
  }

  /*
   * Shows the empty-cart panel once the reconciler has emptied the cart.
   *
   * Whether the cart reads as empty is not decided by anything inside the
   * sections — `.cart__warnings` and `.cart__footer` are always rendered, and
   * an `is-empty` class on the two section *roots* is what shows one and hides
   * the other (component-cart.css). rerender() only swaps innerHTML, so that
   * class would never flip on its own: removing the last Signature Class took
   * its free books with it and left an empty item list, a $0.00 subtotal and a
   * dead checkout button where "your shopping cart is currently empty" belongs.
   *
   * Same toggle cart.js already does on its own removal path (cart.js:178-183),
   * including the drawer — on a drawer-type cart, cart.js is not loaded on this
   * page but this script still is.
   */
  function syncEmptyState(count) {
    var empty = count === 0;
    var items = document.querySelector('cart-items');
    var footer = document.getElementById(FOOTER_ID);
    var drawer = document.querySelector('cart-drawer');

    if (items) items.classList.toggle('is-empty', empty);
    if (footer) footer.classList.toggle('is-empty', empty);
    if (drawer) drawer.classList.toggle('is-empty', empty);
  }

  function setBusy(busy) {
    var items = document.getElementById(ITEMS_ID);
    if (items) items.classList.toggle('cart__items--disabled', busy);
    if (busy) markBusyRows();
    else clearBusyRows();
  }

  function reconcile() {
    if (running) return;
    var plan = readPlan(document);
    if (planSize(plan) === 0) return;
    if (passes >= MAX_PASSES) return;

    running = true;
    passes += 1;
    busyTargets = collectBusyTargets(plan);
    setBusy(true);

    applyPlan(plan)
      .then(rerender)
      .then(function () {
        // The re-render replaced the rows the spinner was on. The same lines
        // are still mid-pass until cart.js has been read back, so mark the new
        // ones rather than letting the row flash back to a ✕ and in again.
        markBusyRows();
        return fetch(window.Shopify.routes.root + 'cart.js', { credentials: 'same-origin' })
          .then(function (response) { return response.json(); });
      })
      .then(function (cart) {
        syncCartCount(cart.item_count);
        syncEmptyState(cart.item_count);

        // The footer (subtotal + Lab gate) listens on the theme's pub/sub
        // channel, so it is told the same way every other cart consumer is
        // rather than being reached into from here.
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-gift-books', cartData: cart });
        }
      })
      .catch(function (error) {
        // A failed pass leaves the cart as it was: the books are simply not
        // gifted yet, and the next load tries again. Nothing is charged wrongly
        // either way, because the price comes from the Cart Transform, not here.
        console.error('cart-gift-books: could not reconcile', error);
      })
      .finally(function () {
        setBusy(false);
        running = false;
        // The re-render brought a fresh plan with it. If the first pass split a
        // paid line or reverted a stale gift, the second one settles it.
        if (planSize(readPlan(document)) > 0) reconcile();
      });
  }

  // Removing a Signature Class is what turns its books back into paid lines, so
  // every cart mutation has to be followed by another look at the plan.
  function attach() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        if (event && event.source === 'cart-gift-books') return;
        passes = 0;
        reconcile();
      });
    }
    reconcile();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attach);
  } else {
    attach();
  }
})();
